/**
 * Cell-level parsing and validation of an uploaded manufacture sheet.
 *
 * Pure: takes the ImportSheet produced by utils/importFileParser plus the column mapping and
 * returns typed rows and per-row errors (identified as file name : row number). Each data row
 * lands either in `rows` or in `errors` — never both, never silently dropped.
 */

import { cellToString, parseImportDate, parseImportQuantity, readCell } from '../importCells'
import { type ImportSheet } from '../importFileParser'
import { exceedsStockQuantityLimit } from '../stockQuantityBounds'
import {
  type ManufactureImportColumnMapping,
  type ManufactureImportError,
  type ParseManufactureRowsResult,
  type ParsedManufactureRow,
} from './types'

/** The app writer (`saveNewManufacture`) throws above this, so parsing must catch it first. */
export const MAX_BATCH_NUMBER_LENGTH = 100

/** `manufactures.quantity` is `numeric(10,4)`: four decimal places. */
const QUANTITY_DECIMALS = 4
const QUANTITY_SCALE = 10 ** QUANTITY_DECIMALS

/**
 * `quantity` exactly as Supabase stores it. Comparisons across the two stores go through this.
 *
 * Postgres receives the shortest round-trip decimal text of the double (what `JSON.stringify`
 * writes) and rounds THAT half away from zero. `Math.round(q * 10^4)` rounds the binary value
 * instead and disagrees on 5th-decimal ties: 0.00015 is 0.000149999… in binary, so it would give
 * 0.0001 where Postgres stores 0.0002.
 */
export const atQuantityScale = (quantity: number): number => {
  const text = String(Math.abs(quantity))
  const sign = quantity < 0 ? -1 : 1
  // Exponent form: below 1e-6 (rounds to 0 at 4 decimals) or at/above 1e21 (no fraction at all).
  if (text.includes('e')) return Math.abs(quantity) < 1 ? 0 : quantity
  const [whole, fraction = ''] = text.split('.')
  if (fraction.length <= QUANTITY_DECIMALS) return quantity
  const roundUp = fraction.charCodeAt(QUANTITY_DECIMALS) >= '5'.charCodeAt(0) ? 1 : 0
  // Integer units are exact below 2^53, and one correctly rounded division gives the nearest double.
  const units = Number(whole) * QUANTITY_SCALE + Number(fraction.slice(0, QUANTITY_DECIMALS)) + roundUp
  return (sign * units) / QUANTITY_SCALE
}

/**
 * How far (relative to max(1, |value|)) a double may sit from its 4-decimal value and still be
 * binary float noise: 2^-40 = 4096 ulps (Number.EPSILON is 2^-52).
 *
 * - It covers the worst-case rounding error of a spreadsheet SUM of up to 4096 cells of 4-decimal
 *   values (n · ε · |sum|); the typical error grows like √n, so far longer sums pass too.
 * - It still refuses every quantity that sits at least 10^-12 · max(1, |value|) from its 4-decimal
 *   value, which is above 2^-40 ≈ 9.09e-13 with a ~10% margin. Concretely: for |value| ≥ 1, EVERY
 *   value written with at most 12 significant digits and a real 5th+ decimal (it is at least 10^-d
 *   from its rounding while |value| < 10^(12-d)); below 1, every value with a nonzero digit in
 *   decimals 5-12.
 *
 * Known limit: a quantity typed with 13-15 significant digits (`999999.9999001`), or with its only
 * extra digits past the 12th decimal (`0.0123000000001`), is as close to its 4-decimal value as
 * accumulated noise is, so the number alone cannot tell them apart and it is read at 4 decimals.
 * No real production quantity carries that precision.
 */
const FLOAT_NOISE_TOLERANCE = 2 ** -40

/**
 * The quantity as BOTH stores will hold it, or `null` when they cannot hold the same value.
 *
 * Firestore keeps the raw double and Supabase rounds to `numeric(10,4)`, so a value that is not
 * already at that scale would leave the stores disagreeing. Binary float noise from a spreadsheet
 * formula (`=0.1*3` → 0.30000000000000004) IS the 4-decimal value the user meant, so it is
 * normalised. A value with more real decimals, or at/over the column's ceiling, is refused — never
 * rounded or clamped (the ATL-960 rule in `stockQuantityBounds`).
 */
export const storableManufactureQuantity = (quantity: number): number | null => {
  const scaled = atQuantityScale(quantity)
  const isFloatNoise = Math.abs(quantity - scaled) <= FLOAT_NOISE_TOLERANCE * Math.max(1, Math.abs(quantity))
  if (!isFloatNoise || scaled <= 0 || exceedsStockQuantityLimit(scaled)) return null
  return scaled
}

export const parseManufactureRows = (
  sheet: ImportSheet,
  mapping: ManufactureImportColumnMapping,
  fileName: string
): ParseManufactureRowsResult => {
  const rows: ParsedManufactureRow[] = []
  const errors: ManufactureImportError[] = []

  for (let index = 1; index < sheet.rows.length; index++) {
    const row = sheet.rows[index]
    const ref = { fileName, rowNumber: sheet.rowNumbers[index] }
    const fail = (code: ManufactureImportError['code'], value = ''): void => {
      errors.push({ ref, code, value })
    }

    const dateCell = readCell(row, mapping.date)
    if (cellToString(dateCell) === '') {
      fail('MISSING_DATE')
      continue
    }
    const date = parseImportDate(dateCell, sheet.date1904)
    if (date === null) {
      fail('INVALID_DATE', cellToString(dateCell))
      continue
    }

    const product = cellToString(readCell(row, mapping.product))
    if (!product) {
      fail('MISSING_PRODUCT')
      continue
    }

    const quantityCell = readCell(row, mapping.quantity)
    if (cellToString(quantityCell) === '') {
      fail('MISSING_QUANTITY')
      continue
    }
    const parsedQuantity = parseImportQuantity(quantityCell)
    if (parsedQuantity === null) {
      fail('INVALID_QUANTITY', cellToString(quantityCell))
      continue
    }
    const quantity = storableManufactureQuantity(parsedQuantity)
    if (quantity === null) {
      fail('QUANTITY_NOT_STORABLE', cellToString(quantityCell))
      continue
    }

    const batchText = cellToString(readCell(row, mapping.batch))
    if (batchText.length > MAX_BATCH_NUMBER_LENGTH) {
      fail('BATCH_TOO_LONG', batchText)
      continue
    }

    const manufactureNo = cellToString(readCell(row, mapping.manufactureNo)) || null
    const ingredient = cellToString(readCell(row, mapping.ingredient)) || null
    let perUnit: number | null = null

    if (ingredient !== null) {
      if (manufactureNo === null) {
        fail('INGREDIENT_WITHOUT_NUMBER', ingredient)
        continue
      }
      const perUnitCell = readCell(row, mapping.ingredientPerUnit)
      if (cellToString(perUnitCell) === '') {
        fail('MISSING_PER_UNIT', ingredient)
        continue
      }
      perUnit = parseImportQuantity(perUnitCell)
      if (perUnit === null) {
        fail('INVALID_PER_UNIT', cellToString(perUnitCell))
        continue
      }
    }

    rows.push({
      ref,
      date,
      product,
      quantity,
      batch: batchText || null,
      manufactureNo,
      ingredient,
      perUnit,
    })
  }

  return { rows, errors }
}
